import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Heart, ShoppingBag, Truck, ShieldCheck, Star, Minus, Plus } from "lucide-react";
import { api, fmtINR } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useShop } from "../lib/shop";
import { toast } from "sonner";
import { ShareButtons } from "../components/ShareButtons";
import { useSEO } from "../lib/useSEO";

export default function ProductDetail() {
  const { idOrSlug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart, toggleWishlist, isWishlisted } = useShop();
  const [p, setP] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: "", comment: "" });
  const [submitting, setSubmitting] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [draft, setDraft] = useState({});

  useEffect(() => {
    api.get(`/products/${idOrSlug}`).then((r) => { setP(r.data); setImgIdx(0); setSelectedVariant(null); setDraft({}); }).catch(() => setP(null));
  }, [idOrSlug]);

  useEffect(() => {
    if (p?.id) api.get(`/products/${p.id}/reviews`).then((r) => setReviews(r.data));
  }, [p?.id]);

  useEffect(() => {
    if (p) {
      // Inject Product JSON-LD (title is handled by useSEO below)
      const existing = document.getElementById("ld-product"); if (existing) existing.remove();
      const ld = document.createElement("script");
      ld.id = "ld-product";
      ld.type = "application/ld+json";
      ld.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Product",
        "name": p.name,
        "description": p.description,
        "image": p.images,
        "brand": { "@type": "Brand", "name": "Joy Pearl" },
        "offers": {
          "@type": "Offer",
          "priceCurrency": "INR",
          "price": p.price,
          "availability": p.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          "url": typeof window !== "undefined" ? window.location.href : "",
        },
        ...(p.rating_count ? {
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": p.rating_avg,
            "reviewCount": p.rating_count,
          },
        } : {}),
      });
      document.head.appendChild(ld);
    }
    return () => {
      const x = document.getElementById("ld-product"); if (x) x.remove();
    };
  }, [p]);

  // Per-page SEO — must be called unconditionally before any early return
  useSEO({
    title: p ? `${p.name} | Joy Pearl` : "Loading… | Joy Pearl",
    description: p
      ? `${(p.description || "").slice(0, 155)} — Buy ${p.name} on Joy Pearl. Cash on Delivery across India.`
      : undefined,
    image: p?.images?.[0] || undefined,
    type: "product",
    path: p?.slug ? `/product/${p.slug}` : undefined,
  });

  // Variant matching effect (must be above early return to keep hook order)
  useEffect(() => {
    if (!p) return;
    const vars = Array.isArray(p.variants) ? p.variants : [];
    if (!vars.length) { setSelectedVariant(null); return; }
    const dims = ["size", "color", "material"].filter((d) => vars.some((v) => (v?.[d] || "").trim()));
    const match = vars.find((v) => dims.every((d) => ((v?.[d] || "").trim()) === (draft[d] || "")));
    setSelectedVariant(match || null);
  }, [draft, p]);

  if (p === null) return <div className="jp-container py-20 text-center text-brand-muted">Loading…</div>;

  const handleAdd = async () => {
    if (!user) { navigate("/login"); return; }
    if (hasVariants && !selectedVariant) { toast.error("Please choose a variant"); return; }
    try { await addToCart(p.id, qty, selectedVariant?.sku); toast.success("Added to bag"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };
  const handleBuyNow = async () => {
    if (!user) { navigate("/login"); return; }
    if (hasVariants && !selectedVariant) { toast.error("Please choose a variant"); return; }
    try { await addToCart(p.id, qty, selectedVariant?.sku); navigate("/checkout"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not proceed"); }
  };
  const handleWish = async () => {
    if (!user) { navigate("/login"); return; }
    try { const w = await toggleWishlist(p.id); toast.success(w ? "Saved" : "Removed"); }
    catch { toast.error("Could not update"); }
  };
  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login"); return; }
    setSubmitting(true);
    try {
      await api.post(`/products/${p.id}/reviews`, { product_id: p.id, ...reviewForm });
      toast.success("Review posted");
      setReviewForm({ rating: 5, title: "", comment: "" });
      const { data: rs } = await api.get(`/products/${p.id}/reviews`); setReviews(rs);
      const { data: pp } = await api.get(`/products/${p.id}`); setP(pp);
    } catch { toast.error("Could not submit review"); }
    finally { setSubmitting(false); }
  };

  const discount = p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0;
  const wished = isWishlisted(p.id);
  const variants = Array.isArray(p.variants) ? p.variants : [];
  const hasVariants = variants.length > 0;
  // Build dimension sets
  const dimensions = ["size", "color", "material"].filter((d) => variants.some((v) => (v?.[d] || "").trim()));
  const variantValues = (dim) => Array.from(new Set(variants.map((v) => (v?.[dim] || "").trim()).filter(Boolean)));
  const setDim = (dim, val) => setDraft((d) => ({ ...d, [dim]: val }));
  const effectivePrice = (selectedVariant ? (p.price + (selectedVariant.additional_price || 0)) : p.price);
  const effectiveStock = selectedVariant ? (selectedVariant.stock || 0) : p.stock;

  return (
    <div className="jp-container py-8 md:py-12" data-testid="product-detail-page">
      <nav className="text-xs uppercase tracking-[0.2em] text-brand-muted mb-6">
        <Link to="/" className="hover:text-brand-primary">Home</Link> /{" "}
        <Link to="/products" className="hover:text-brand-primary">Shop</Link> /{" "}
        <Link to={`/products?category=${p.category_id}`} className="hover:text-brand-primary">{p.category_name}</Link>
      </nav>
      <div className="grid md:grid-cols-2 gap-8 lg:gap-16">
        <div>
          <div className="aspect-square bg-brand-blush overflow-hidden">
            <img src={p.images?.[imgIdx] || ""} alt={p.name} className="h-full w-full object-cover" data-testid="product-image-main" />
          </div>
          {p.images?.length > 1 && (
            <div className="mt-4 flex gap-3">
              {p.images.map((img, i) => (
                <button key={i} onClick={() => setImgIdx(i)} className={`h-20 w-20 border ${imgIdx === i ? "border-brand-primary" : "border-brand-line"} overflow-hidden`}>
                  <img src={img} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="jp-eyebrow mb-3">{p.category_name}</p>
          <h1 className="font-serif text-3xl md:text-4xl text-brand-ink leading-tight" data-testid="product-name">{p.name}</h1>
          <div className="mt-3 flex items-center gap-2 text-sm text-brand-muted">
            <Star size={14} className="text-brand-primary" fill="currentColor" />
            <span>{p.rating_avg?.toFixed(1) || "—"}</span>
            <span className="opacity-50">({p.rating_count || 0} reviews)</span>
            <span className="opacity-40">•</span>
            <span>Sold by {p.seller_name}</span>
          </div>
          <div className="mt-6 flex items-baseline gap-3">
            <span className="text-3xl font-semibold text-brand-ink" data-testid="product-price">{fmtINR(effectivePrice)}</span>
            {p.mrp > effectivePrice && (
              <>
                <span className="text-brand-muted line-through">{fmtINR(p.mrp)}</span>
                <span className="jp-badge bg-brand-primary text-white">{Math.round(((p.mrp - effectivePrice) / p.mrp) * 100)}% off</span>
              </>
            )}
          </div>
          <p className="text-xs text-brand-muted mt-1">Inclusive of all taxes • COD available</p>

          <p className="mt-6 text-brand-ink/80 leading-relaxed">{p.description}</p>

          {hasVariants && (
            <div className="mt-6 space-y-4" data-testid="variants-selector">
              {dimensions.map((dim) => (
                <div key={dim}>
                  <p className="jp-eyebrow mb-2">{dim}</p>
                  <div className="flex flex-wrap gap-2">
                    {variantValues(dim).map((val) => {
                      const active = draft[dim] === val;
                      // Disable if no variant exists with this val + other already-selected dims
                      const hypothesis = { ...draft, [dim]: val };
                      const exists = variants.some((v) => dimensions.every((d) => !hypothesis[d] || (v?.[d] || "").trim() === hypothesis[d]));
                      return (
                        <button
                          key={val}
                          type="button"
                          disabled={!exists}
                          onClick={() => setDim(dim, val)}
                          className={`px-4 py-2 text-sm border transition-colors ${
                            active
                              ? "border-brand-primary bg-brand-primary text-white"
                              : exists
                                ? "border-brand-line hover:border-brand-primary text-brand-ink"
                                : "border-brand-line text-brand-muted opacity-50 cursor-not-allowed line-through"
                          }`}
                          data-testid={`variant-${dim}-${val.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {val}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {selectedVariant ? (
                <p className="text-xs text-emerald-700" data-testid="variant-selected-label">
                  ✓ Selected: SKU {selectedVariant.sku} · Stock {selectedVariant.stock}
                </p>
              ) : (
                <p className="text-xs text-brand-muted" data-testid="variant-pick-hint">Select all options above to enable Add to Bag.</p>
              )}
            </div>
          )}

          {effectiveStock > 0 ? (
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-emerald-700">In stock — {effectiveStock} left</p>
          ) : (
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-red-700">Out of stock</p>
          )}

          <div className="mt-6 flex items-center gap-4">
            <div className="inline-flex items-center border border-brand-line">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3" aria-label="Decrease" data-testid="qty-decrease"><Minus size={14}/></button>
              <span className="px-4 text-sm font-medium" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-3" aria-label="Increase" data-testid="qty-increase"><Plus size={14}/></button>
            </div>
            <button onClick={handleWish} className={`p-3 border border-brand-line ${wished ? "text-brand-primary" : ""}`} aria-label="Wishlist" data-testid="product-wishlist">
              <Heart size={18} fill={wished ? "currentColor" : "none"} />
            </button>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button onClick={handleAdd} disabled={effectiveStock <= 0 || (hasVariants && !selectedVariant)} className="jp-btn-outline w-full" data-testid="add-to-bag-btn">
              <ShoppingBag size={16} className="mr-2"/> Add to Bag
            </button>
            <button onClick={handleBuyNow} disabled={effectiveStock <= 0 || (hasVariants && !selectedVariant)} className="jp-btn-primary w-full" data-testid="buy-now-btn">
              Buy Now
            </button>
          </div>

          <ul className="mt-8 grid grid-cols-2 gap-4 text-xs text-brand-muted">
            <li className="flex items-center gap-2"><Truck size={14} className="text-brand-primary"/> Free shipping over ₹499</li>
            <li className="flex items-center gap-2"><ShieldCheck size={14} className="text-brand-primary"/> COD with OTP confirmation</li>
          </ul>

          <ShareButtons title={p.name} image={p.images?.[0]} />
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-16 border-t border-brand-line pt-12">
        <h2 className="font-serif text-2xl md:text-3xl text-brand-ink mb-6">Reviews</h2>
        <div className="grid md:grid-cols-[1fr_360px] gap-10">
          <div className="space-y-6" data-testid="reviews-list">
            {reviews.length === 0 && <p className="text-sm text-brand-muted">No reviews yet. Be the first!</p>}
            {reviews.map((r) => (
              <div key={r.id} className="border-b border-brand-line pb-4">
                <div className="flex items-center gap-1 text-brand-primary">
                  {Array.from({length: 5}).map((_, i) => <Star key={i} size={14} fill={i < r.rating ? "currentColor" : "none"} />)}
                </div>
                <p className="font-medium mt-1">{r.title}</p>
                <p className="text-sm text-brand-muted mt-1">{r.comment}</p>
                <p className="text-xs text-brand-muted mt-1">— {r.user_name}</p>
              </div>
            ))}
          </div>
          <form onSubmit={submitReview} className="bg-white border border-brand-line p-6" data-testid="review-form">
            <h3 className="font-serif text-lg mb-4">Write a review</h3>
            <label className="text-xs uppercase tracking-[0.2em] text-brand-muted">Your rating</label>
            <div className="mt-2 flex gap-1">
              {Array.from({length: 5}).map((_, i) => (
                <button key={i} type="button" onClick={() => setReviewForm((f) => ({...f, rating: i+1}))} aria-label={`${i+1} star`}>
                  <Star size={22} className={i < reviewForm.rating ? "text-brand-primary" : "text-brand-line"} fill={i < reviewForm.rating ? "currentColor" : "none"} />
                </button>
              ))}
            </div>
            <input className="jp-input mt-4" placeholder="Title (optional)" value={reviewForm.title} onChange={(e) => setReviewForm((f) => ({...f, title: e.target.value}))} data-testid="review-title"/>
            <textarea className="jp-input mt-4 min-h-[100px]" placeholder="Your thoughts on this product" value={reviewForm.comment} onChange={(e) => setReviewForm((f) => ({...f, comment: e.target.value}))} data-testid="review-comment"/>
            <button type="submit" disabled={submitting} className="jp-btn-primary w-full mt-4" data-testid="review-submit">{submitting ? "Posting…" : "Post Review"}</button>
          </form>
        </div>
      </section>
    </div>
  );
}
